import type { RenderField } from '@/components/blocks/collection/types';
import { resolveImagePresentation } from '@/components/blocks/primitives/image-presentation';
import { CollectionCardFieldRenderer } from '@/components/blocks/renderers/collection-card-renderer';
import { ImageWithLoading } from '@/components/blocks/section-image/image-with-loading';
import type {
  CollectionCardConfig,
  CollectionCardField,
  CollectionItem,
} from '@/lib/blocks/collection/blocks';
import { getEnabledFields } from '@/lib/blocks/collection/blocks';
import {
  collectionCardMetaLabelClass,
  isValidDateString,
  readCollectionFieldNames,
} from '@/lib/blocks/collection/field-utils';
import type {
  CollectionFieldRendererVariant,
  PinnedBlogFieldOptions,
} from '@/lib/blocks/collection/index-collection/field-renderer-types';
import { readImageUrl, readTrimmedString } from '@/lib/prop-utils';
import { cn } from '@/lib/utils';
import {
  BadgeListField,
  renderBlogMetaRow,
  renderPinnedBlogMetaRow,
  renderRichSummary,
  renderRichTitle,
  renderWidgetSummary,
  renderWidgetTitle,
} from './shared';

export interface BuildBlogCollectionRenderFieldOptions {
  item: CollectionItem;
  cardConfig: CollectionCardConfig;
  variant: CollectionFieldRendererVariant;
  pinned?: PinnedBlogFieldOptions;
}

export function buildBlogCollectionRenderField({
  item,
  cardConfig,
  variant,
  pinned = {},
}: BuildBlogCollectionRenderFieldOptions): RenderField {
  const align = cardConfig.align;
  const enabledFields = getEnabledFields(cardConfig.fields);
  const showAuthors = enabledFields.some((field) => field.slot === 'authors');
  const showPublishedAt = enabledFields.some((field) => field.slot === 'publishedAt');
  const metaSlots = new Set(['authors', 'publishedAt']);
  const firstMetaSlot = enabledFields.find((field) => metaSlots.has(field.slot))?.slot ?? null;

  return (field, value) => {
    switch (field.slot) {
      case 'image': {
        if (variant === 'index') {
          const url = readImageUrl(value);
          if (!url) return null;

          return (
            <ImageWithLoading
              image={{ url }}
              alt={readTrimmedString(item.imageTitle) ?? 'Blog'}
              className={cn(
                'w-full rounded-lg object-cover',
                resolveImagePresentation({ aspect: field.blockProps?.aspect }).aspectClassName,
              )}
              inlineSvg={false}
            />
          );
        }

        return <CollectionCardFieldRenderer field={field} value={value} align={align} />;
      }

      case 'topics': {
        const topics = readCollectionFieldNames(item.topics);
        if (topics.length === 0) return null;

        return (
          <BadgeListField
            names={topics}
            align={align}
            widget={variant === 'widget'}
            overlay={variant === 'pinned'}
            size={variant === 'pinned' ? 'md' : 'sm'}
            className={variant === 'widget' ? 'pt-2' : undefined}
          />
        );
      }

      case 'title': {
        if (variant === 'widget') {
          return renderWidgetTitle(item.title);
        }

        if (variant === 'pinned') {
          return renderRichTitle(item.title, pinned.titleClassName ?? 'text-h1');
        }

        return renderRichTitle(item.title, 'text-xl md:text-2xl');
      }

      case 'summary': {
        if (variant === 'widget') {
          return renderWidgetSummary(item.summary, 'text-lg leading-normal');
        }

        if (variant === 'pinned') {
          return renderRichSummary(
            item.summary,
            pinned.summaryClassName ?? 'line-clamp-3 text-base',
          );
        }

        return renderRichSummary(item.summary, 'line-clamp-3 text-base');
      }

      case 'authors':
      case 'publishedAt':
        return renderBlogMetaSlot({
          field,
          item,
          variant,
          firstMetaSlot,
          showAuthors,
          showPublishedAt,
        });

      case 'button':
        return <CollectionCardFieldRenderer field={field} value={value} align={align} />;

      default:
        return null;
    }
  };
}

function renderBlogMetaSlot({
  field,
  item,
  variant,
  firstMetaSlot,
  showAuthors,
  showPublishedAt,
}: {
  field: CollectionCardField;
  item: CollectionItem;
  variant: CollectionFieldRendererVariant;
  firstMetaSlot: string | null;
  showAuthors: boolean;
  showPublishedAt: boolean;
}) {
  if (field.slot !== firstMetaSlot || (!showAuthors && !showPublishedAt)) return null;

  if (variant === 'widget') {
    return renderBlogMetaRow({
      item,
      showAuthors,
      showPublishedAt,
      includeInvalidDateFallback: true,
      className: collectionCardMetaLabelClass,
    });
  }

  if (variant === 'pinned') {
    return renderPinnedBlogMetaRow({
      item,
      showAuthors,
      showPublishedAt,
    });
  }

  const authors = showAuthors
    ? readCollectionFieldNames(item.authors).map((name) => name.toUpperCase())
    : [];
  const publishedAtValue = item.publishedAt;
  const hasValidPublishedAt = showPublishedAt && isValidDateString(publishedAtValue);
  if (authors.length === 0 && !hasValidPublishedAt) return null;

  return renderBlogMetaRow({
    item,
    showAuthors,
    showPublishedAt,
    includeInvalidDateFallback: false,
    className: 'text-xs uppercase leading-5 tracking-[0.08em]',
  });
}
